import {
  AutoSelection,
  Combobox,
  Icon,
  InlineStack,
  Listbox,
  Tag,
} from "@shopify/polaris"
import { SelectIcon } from "@shopify/polaris-icons"
import classNames from "classnames"
import {
  filterOptions,
  Option,
  option2map,
  useLoadMore,
  useScrollRef,
} from "polaris-enhance"
import { useCallback, useMemo, useState } from "react"

import { useValidate } from "../global"
import { ProTextFieldProps } from "../ProTextFiled/ProTextField"
import Styles from "./index.module.scss"

interface ProComboSelectMultiProps extends Pick<
  ProTextFieldProps,
  | "suffix"
  | "prefix"
  | "helpText"
  | "labelAction"
  | "requiredIndicator"
  | "placeholder"
> {
  label?: string
  value: string[]
  onChange(value: string[]): void
  options: Option[]
  required?: boolean
  id?: string
}
export function ProComboSelectMulti(props: ProComboSelectMultiProps) {
  const {
    options: deselectedOptions,
    label,
    value = [],
    required,
    onChange,
    suffix = <Icon source={SelectIcon}></Icon>,
    ...textFieldProps
  } = props

  const [inputValue, setInputValue] = useState("")
  const [isClickingSuffix, setIsClickingSuffix] = useState(false)
  const { scrollToRef, scrooledRef } = useScrollRef()
  const options = useMemo(() => {
    return filterOptions(deselectedOptions, inputValue)
  }, [deselectedOptions, inputValue])

  const { renderOptions, handleScrollBottom } = useLoadMore({ options })
  const updateSelection = useCallback(
    (val: string) => {
      const normalizedValue = value.map(String)
      if (normalizedValue.includes(val)) {
        onChange(normalizedValue.filter((item) => item !== val))
      } else {
        onChange([...normalizedValue, val])
      }
      scrollToRef()
    },
    [onChange, scrollToRef, value],
  )

  const deselectedOptionMap = useMemo(
    () =>
      option2map(
        deselectedOptions.map((item) => ({
          ...item,
          value: String(item.value),
        })),
      ),
    [deselectedOptions],
  )

  const contentMarkup =
    value.length > 0 ? (
      <InlineStack gap={"200"}>
        {value.map((v) => {
          const item = deselectedOptionMap[v]
          return (
            <Tag
              key={v}
              onRemove={() => {
                onChange(value.filter((selected) => selected !== v))
              }}
            >
              {item?.label ?? v}
            </Tag>
          )
        })}
      </InlineStack>
    ) : null

  const { id, error, handleFocus, handleBlur } = useValidate({
    value,
    onlyRequired: required,
    id: textFieldProps.id,
  })

  // 处理点击 suffix 触发下拉框
  const handleSuffixMouseDown = useCallback(
    (event: React.MouseEvent) => {
      // 阻止事件冒泡，防止触发失焦事件
      event.preventDefault()
      event.stopPropagation()
      setIsClickingSuffix(true)

      // 通过 DOM 查询找到输入框并触发点击
      const inputElement = document.getElementById(id)
      if (inputElement) {
        inputElement.focus()
        inputElement.click()
      }
    },
    [id],
  )

  const handleSuffixMouseUp = useCallback(() => {
    setIsClickingSuffix(false)
  }, [])

  return (
    <div
      className={classNames("space-y-2", Styles.TextFieldSuffix)}
      ref={scrooledRef}
    >
      <Combobox
        allowMultiple
        activator={
          <Combobox.TextField
            {...textFieldProps}
            id={id}
            error={error}
            onChange={setInputValue}
            label={label}
            value={inputValue}
            autoComplete="off"
            // verticalContent={contentMarkup}
            onBlur={() => {
              // 如果正在点击 suffix，不触发失焦事件
              if (!isClickingSuffix) {
                handleBlur()
              }
            }}
            onFocus={handleFocus}
            suffix={
              suffix != null ? (
                <div
                  onMouseDown={handleSuffixMouseDown}
                  onMouseUp={handleSuffixMouseUp}
                  className={classNames("cursor-pointer")}
                  style={{ "--p-space-300": "0" }}
                >
                  {suffix}
                </div>
              ) : undefined
            }
          />
        }
        onScrolledToBottom={handleScrollBottom}
        onClose={() => {
          setInputValue("")
          handleBlur()
        }}
      >
        {renderOptions.length > 0 ? (
          <Listbox
            onSelect={updateSelection}
            autoSelection={AutoSelection.None}
          >
            {renderOptions.map((option) => {
              const { label } = option
              const optionValue = String(option.value)
              return (
                <Listbox.Option
                  key={optionValue}
                  value={optionValue}
                  selected={value.includes(optionValue)}
                  accessibilityLabel={label}
                >
                  <Listbox.TextOption selected={value.includes(optionValue)}>
                    {option.label}
                  </Listbox.TextOption>
                </Listbox.Option>
              )
            })}
          </Listbox>
        ) : (
          <div className="px-2 py-1">{t("NoResults")}</div>
        )}
      </Combobox>
      {contentMarkup}
    </div>
  )
}
